import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import BrandMark from "./BrandMark";

/* AppScreen chrome for inner pages (matches owner mockup):
     ← My Social Pulse™ · 🔔 · avatar
       {Page title}          (big serif)
   Only renders on mobile (md:hidden). Desktop keeps the site Navbar. */

export function AppTopBar({ title, back = true, right = null, testid = "app-top-bar" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  return (
    <div className="md:hidden sticky top-0 z-30 bg-background/95 backdrop-blur-xl border-b border-border safe-t" data-testid={testid}>
      <div className="flex items-center gap-2 px-3 h-14">
        {back ? (
          <button onClick={() => nav(-1)} aria-label="Back" data-testid={`${testid}-back`}
            className="w-10 h-10 rounded-full hover:bg-secondary/60 inline-flex items-center justify-center tap">
            <ChevronLeft className="w-5 h-5 text-primary" strokeWidth={2} />
          </button>
        ) : <span className="w-10" />}
        <div className="flex-1 min-w-0 flex justify-center">
          <BrandMark size="sm" subline to="/" testid={`${testid}-brand`} />
        </div>
        <Link to="/notifications" aria-label="Notifications" data-testid={`${testid}-bell`}
          className="w-10 h-10 rounded-full hover:bg-secondary/60 inline-flex items-center justify-center relative tap">
          <Bell className="w-5 h-5 text-primary" strokeWidth={2} />
        </Link>
        <Link to={user ? "/profile" : "/login"} data-testid={`${testid}-avatar`} aria-label="Profile"
          className="w-9 h-9 rounded-full bg-primary text-primary-foreground font-serif font-800 text-sm inline-flex items-center justify-center overflow-hidden tap">
          {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "?"}
        </Link>
      </div>
      {title && (
        <div className="px-5 pb-3 flex items-center justify-between gap-3">
          <h1 className="font-serif font-800 text-3xl tracking-tight text-primary leading-tight" data-testid={`${testid}-title`}>{title}</h1>
          {right}
        </div>
      )}
    </div>
  );
}

/* Full-page wrapper — useful for new screens built from scratch. Legacy pages
   that already have their own hero should use <AppTopBar/> instead. */
export default function AppScreen({ title, children, back = true, right = null, testid }) {
  return (
    <div className="min-h-screen bg-background" data-testid={testid || "app-screen"}>
      <AppTopBar title={title} back={back} right={right} />
      {title && (
        <div className="hidden md:block max-w-7xl mx-auto px-5 lg:px-8 pt-8 pb-4">
          <h1 className="font-serif font-800 text-4xl tracking-tight text-primary" data-testid="app-screen-title-desktop">{title}</h1>
        </div>
      )}
      {children}
    </div>
  );
}

/* Tab pill row used across inner mockups (Trips: Upcoming/Past/Saved, Documents:
   All/Passport/Visas/Tickets/Insurance, Expenses: All/This Trip/By Category, etc). */
export function TabPills({ value, onChange, items, testid = "app-tab-pills" }) {
  return (
    <div className="flex items-center gap-1 p-1 bg-secondary/60 rounded-full mx-auto max-w-fit" data-testid={testid}>
      {items.map((it) => (
        <button key={it.value || it} onClick={() => onChange(it.value || it)}
          data-testid={`${testid}-${(it.value || it).toString().toLowerCase().replace(/\s/g, "-")}`}
          className={`min-h-[40px] px-5 py-1.5 rounded-full text-sm font-700 transition-all tap ${
            value === (it.value || it)
              ? "bg-primary text-primary-foreground shadow-warm-sm"
              : "text-muted-foreground hover:text-primary"
          }`}>
          {it.label || it}
        </button>
      ))}
    </div>
  );
}
